import { useEffect, useRef } from 'react'
import { fireReactGAEvent } from '../components/Questions/utils'
import { AppFormEventParams } from '../services/events-service'
import { useAppFormEventsService } from './useAppFormEventsService'
import { AmplitudeEvents, trackAmplitudeEvent } from '../utils/amplitude-utils'
import { toSnakeCase } from '../utils/string-utils'

type UseQuestionVisibilityTrackingOptions = {
  eventsServiceUrl?: string
  appFormEventParams?: AppFormEventParams
}

/**
 * Sends the Question Visible event (GA, events API and Amplitude) once each time a question is shown.
 * It only fires again when questionKey changes, not when the form re-renders.
 */
export const useQuestionVisibilityTracking = (questionKey: string, options?: UseQuestionVisibilityTrackingOptions) => {
  const eventsService = useAppFormEventsService(options?.eventsServiceUrl, options?.appFormEventParams)

  // The events service is rebuilt whenever the form's event params change, so read the latest one
  // from a ref rather than depending on it. Depending on it re-sent the event on every form re-render.
  const eventsServiceRef = useRef(eventsService)
  eventsServiceRef.current = eventsService

  useEffect(() => {
    const label = `qst.${questionKey}`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsServiceRef.current?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })
    trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(label) })
  }, [questionKey])
}
